import { useTenantSetting } from "@nestoca/multi-tenant";
import {
  Flex,
  Modal,
  ModalProps,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@nestoca/ui";
import { useGetAccount } from "@shared/api/hooks/account";
import { TOAST_AUTOCLOSE_DELAY_IN_MS } from "@shared/constants";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { CommunicationPreferences } from "../../navbar/burger-menu/communication-preferences/communication-preferences";

import { SettingsForm } from "./settings-form";
import styles from "./user-settings-modal.module.scss";

type UserSettingsModalProps = Pick<ModalProps, "visible" | "onClose"> & {
  handleLogout: () => void;
};

export function UserSettingsModal({
  visible,
  onClose,
}: UserSettingsModalProps) {
  const { t } = useTranslation();
  const { data: account, isPending, isError } = useGetAccount();
  const { value: communicationPreference } = useTenantSetting(
    "communicationPreference"
  );

  if (visible && isError) {
    toast(t("getAccountError"), {
      type: "error",
      autoClose: TOAST_AUTOCLOSE_DELAY_IN_MS,
      toastId: "use-get-account-error",
    });
    onClose?.();
  }

  if (!account || isPending) {
    return <Spinner />;
  }

  return (
    <Modal
      visible={visible}
      className={styles["user-settings-modal"]}
      closeOnOutsideClick
      onClose={onClose}
      e2ePrefix="user-settings"
    >
      <Flex as="form" direction="column" className={styles.form}>
        <Modal.Header>
          <h1 className={styles["header"]}>{t("settings")}</h1>
        </Modal.Header>
        <Modal.Body>
          <Tabs className={styles["tabs"]}>
            <TabList>
              <Tab e2ePrefix="personal-info-tab">{t("personalInfo")}</Tab>
              {communicationPreference && (
                <Tab e2ePrefix="communication-preferences-tab">
                  {t("communicationPreferences.title")}
                </Tab>
              )}
            </TabList>
            <TabPanels>
              <TabPanel key="personal-info" className={styles["tab-panel"]}>
                <SettingsForm />
              </TabPanel>
              {communicationPreference && (
                <TabPanel
                  key="communication-preferences"
                  className={styles["tab-panel"]}
                >
                  <CommunicationPreferences />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </Modal.Body>
      </Flex>
    </Modal>
  );
}
